import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, unref as _unref, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "panel"
}
const _hoisted_2 = {
  key: 0,
  class: "shop-field"
}
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  value: 0,
  disabled: ""
}
const _hoisted_5 = {
  key: 1,
  value: 0,
  disabled: ""
}
const _hoisted_6 = ["value"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "icon" }

import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useAuthStore } from '@/store/auth';
  import { useGlobalMenu } from '@/composables/useGlobalMenu';
  import { fetchShopDetail, fetchShopList } from '@/service/base';
  import type { ShopDetailModel } from '@/model/shop';
  import { resolveAccessibleShopIds } from '@/constants/auth';

  
export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalQuickMenu',
  setup(__props) {

  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const isOpen = ref(false);
  const menuRef = ref<HTMLElement | null>(null);
  const visibleShops = ref<ShopDetailModel[]>([]);
  const selectedShopId = ref(authStore.selectedShopId || 0);
  const shopLoading = ref(false);
  let shopLoadRequest = 0;
  const showShopSelector = computed(() => {
    const accessibleShopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
    return accessibleShopIds === null || accessibleShopIds.length > 0;
  });

  const { entries } = useGlobalMenu();

  const canShow = computed(
    () =>
      authStore.isLoggedIn &&
      authStore.hasSystemAccess &&
      route.path !== '/login' &&
      route.path !== '/forbidden',
  );

  watch(
    () => route.fullPath,
    () => {
      isOpen.value = false;
    },
  );

  const toggleOpen = (): void => {
    isOpen.value = !isOpen.value;
  };

  const handleDocumentClick = (event: MouseEvent): void => {
    if (!isOpen.value) return;
    const target = event.target as Node | null;
    if (menuRef.value && target && !menuRef.value.contains(target)) {
      isOpen.value = false;
    }
  };

  const loadScopedShopDetails = async (shopIds: number[]): Promise<ShopDetailModel[]> => {
    const responses = await Promise.all(
      shopIds.map((shopId) => fetchShopDetail({ shopId }).catch(() => undefined)),
    );
    return responses.flatMap((response) =>
      response?.code === 200 && response.result && Number(response.result.shopStatus) === 0
        ? [response.result]
        : [],
    );
  };

  const loadVisibleShops = async (): Promise<void> => {
    const request = ++shopLoadRequest;
    if (!showShopSelector.value) {
      visibleShops.value = [];
      selectedShopId.value = 0;
      return;
    }
    shopLoading.value = true;
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 100,
        data: { shopStatus: 0 },
      });
      if (request !== shopLoadRequest) return;
      if (response.code !== 200) throw new Error(response.msg || '加载餐厅失败');
      const accessibleShopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
      let shops = (response.result?.list || []) as ShopDetailModel[];
      if (accessibleShopIds?.length && shops.length === 0) {
        shops = await loadScopedShopDetails(accessibleShopIds);
      }
      if (request !== shopLoadRequest) return;
      visibleShops.value = accessibleShopIds
        ? shops.filter((shop) => accessibleShopIds.includes(Number(shop.shopId)))
        : shops;
      const selected =
        visibleShops.value.find((shop) => Number(shop.shopId) === authStore.selectedShopId) ||
        visibleShops.value[0];
      selectedShopId.value = selected ? Number(selected.shopId) : 0;
      if (selected && selectedShopId.value !== authStore.selectedShopId) {
        authStore.selectShop(selectedShopId.value);
        window.location.reload();
      }
    } catch (error) {
      const accessibleShopIds = resolveAccessibleShopIds(authStore.user?.roles || []);
      const fallbackShops = accessibleShopIds?.length
        ? await loadScopedShopDetails(accessibleShopIds)
        : [];
      if (request !== shopLoadRequest) return;
      visibleShops.value = fallbackShops;
      if (!visibleShops.value.length) console.error('加载授权餐厅失败', error);
    } finally {
      if (request === shopLoadRequest) shopLoading.value = false;
    }
  };

  watch(
    [() => authStore.isLoggedIn, () => (authStore.user?.roles || []).slice().sort().join('|')],
    ([isLoggedIn]) => {
      if (!isLoggedIn) {
        shopLoadRequest++;
        visibleShops.value = [];
        selectedShopId.value = 0;
        shopLoading.value = false;
        return;
      }
      void loadVisibleShops();
    },
    { immediate: true },
  );

  onMounted(() => {
    document.addEventListener('click', handleDocumentClick);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleDocumentClick);
  });

  const go = async (path: string): Promise<void> => {
    await router.push(path);
  };

  const changeShop = (): void => {
    if (!visibleShops.value.some((shop) => Number(shop.shopId) === selectedShopId.value)) return;
    authStore.selectShop(selectedShopId.value);
    isOpen.value = false;
    window.location.reload();
  };

  const logout = async (): Promise<void> => {
    await authStore.logout();
    await router.replace('/login');
  };

return (_ctx: any,_cache: any) => {
  return (canShow.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "menuRef",
        ref: menuRef,
        class: "quick-menu"
      }, [
        _createElementVNode("button", {
          class: "fab",
          onClick: toggleOpen
        }, _toDisplayString(isOpen.value ? '×' : '≡'), 1),
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _cache[2] || (_cache[2] = _createElementVNode("div", { class: "panel-title" }, "快速菜单", -1)),
              (showShopSelector.value)
                ? (_openBlock(), _createElementBlock("label", _hoisted_2, [
                    _cache[1] || (_cache[1] = _createElementVNode("span", null, "当前餐厅", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedShopId).value = $event)),
                      class: "shop-select",
                      disabled: shopLoading.value || visibleShops.value.length === 0,
                      onChange: changeShop
                    }, [
                      (shopLoading.value)
                        ? (_openBlock(), _createElementBlock("option", _hoisted_4, "餐厅加载中..."))
                        : (visibleShops.value.length === 0)
                          ? (_openBlock(), _createElementBlock("option", _hoisted_5, "暂无可用餐厅"))
                          : _createCommentVNode("", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleShops.value, (shop) => {
                        return (_openBlock(), _createElementBlock("option", {
                          key: shop.shopId,
                          value: shop.shopId
                        }, _toDisplayString(shop.shopName), 9, _hoisted_6))
                      }), 128))
                    ], 40, _hoisted_3), [
                      [
                        _vModelSelect,
                        selectedShopId.value,
                        void 0,
                        { number: true }
                      ]
                    ])
                  ]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(entries), (entry) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: entry.name,
                  class: _normalizeClass(["menu-item", { active: _unref(route).path === entry.path }]),
                  onClick: ($event: any) => (go(entry.path))
                }, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(entry.icon), 1),
                  _createElementVNode("span", null, _toDisplayString(entry.title), 1)
                ], 10, _hoisted_7))
              }), 128)),
              _createElementVNode("button", {
                class: "menu-item logout",
                onClick: logout
              }, "退出登录")
            ]))
          : _createCommentVNode("", true)
      ], 512))
    : _createCommentVNode("", true)
}
}

})